import { useState, useRef } from 'react';
import { Camera, User, MapPin, Phone, Globe, Save, Loader2 } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    phone: user?.phone || '',
    website: user?.website || '',
  });

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      // Pre-check: ensure we have a valid token before uploading
      try {
        await api.getMe();
      } catch {
        // Token might be expired, the interceptor will refresh it
        // Wait a moment for the refresh to complete
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      const uploadResult = await api.uploadFile(file);
      const avatarUrl = `/api/files/${uploadResult.id}`;
      await api.updateSellerProfile({ avatar_url: avatarUrl });
      updateUser({ avatar_url: avatarUrl });
      toast.success('Foto de perfil actualizada');
    } catch (err) {
      toast.error(formatApiError(err) || 'Error al subir imagen. Recarga la pagina e intenta de nuevo.');
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.updateSellerProfile(form);
      updateUser(updated);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const displayAvatar = avatarPreview || user?.avatar_url;

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 font-[Outfit]">
          Configuraci&oacute;n
        </h1>
        <p className="text-zinc-500 mt-1">Administra tu perfil y preferencias</p>
      </div>

      {/* Avatar Section */}
      <div className="glass-card p-6 mb-6" data-testid="avatar-section">
        <h2 className="text-lg font-medium text-zinc-100 mb-4">Foto de perfil</h2>
        <div className="flex items-center gap-6">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-zinc-800 border-2 border-white/10 flex items-center justify-center">
              {displayAvatar ? (
                <img
                  src={displayAvatar}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  data-testid="avatar-image"
                />
              ) : (
                <span className="text-3xl font-semibold text-zinc-500" data-testid="avatar-initial">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-500 flex items-center justify-center border-2 border-[#121215] transition-colors"
              data-testid="upload-avatar-btn"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarSelect}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div>
            <p className="text-sm text-zinc-300">Sube una foto de perfil</p>
            <p className="text-xs text-zinc-500 mt-1">JPG, PNG o WebP. M&aacute;x 5MB.</p>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="glass-card p-6 mb-6" data-testid="profile-form">
        <h2 className="text-lg font-medium text-zinc-100 mb-4">Informaci&oacute;n del perfil</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-400 mb-2 block">
              <User className="w-3.5 h-3.5 inline mr-1.5" />
              Nombre
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Tu nombre o nombre del criadero"
              className="bg-white/5 border-white/10 text-zinc-100"
              data-testid="profile-name-input"
            />
          </div>

          <div>
            <Label className="text-zinc-400 mb-2 block">Bio / Descripci&oacute;n</Label>
            <Textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Cu&eacute;ntanos sobre ti o tu criadero..."
              className="bg-white/5 border-white/10 text-zinc-100"
              rows={3}
              data-testid="profile-bio-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">
                <MapPin className="w-3.5 h-3.5 inline mr-1.5" />
                Ubicaci&oacute;n
              </Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Ciudad, Estado"
                className="bg-white/5 border-white/10 text-zinc-100"
                data-testid="profile-location-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">
                <Phone className="w-3.5 h-3.5 inline mr-1.5" />
                Tel&eacute;fono
              </Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+52 ..."
                className="bg-white/5 border-white/10 text-zinc-100"
                data-testid="profile-phone-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-zinc-400 mb-2 block">
              <Globe className="w-3.5 h-3.5 inline mr-1.5" />
              Sitio web
            </Label>
            <Input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://..."
              className="bg-white/5 border-white/10 text-zinc-100"
              data-testid="profile-website-input"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar cambios
          </Button>
        </div>
      </div>
    </div>
  );
}
